import {
  differenceInHours,
  format,
  getHours,
  parseISO,
  subDays,
} from 'date-fns'
import { tz } from '@date-fns/tz'
import { safeRound } from './conversions'
import type {
  BucketedForecast,
  DailyForecast,
  LocationWithForecastAndReport,
  NOAATextForecastResponse,
  SortOption,
} from '@/types/core'

/**
 * Convert Celsius to Fahrenheit
 */
export function celsiusToFahrenheit(celsius: number): number {
  return (celsius * 9) / 5 + 32
}

/**
 * Convert millimeters to inches
 */
export function millimetersToInches(mm: number): number {
  return mm / 25.4
}

/**
 * Converts a value in meters to feet.
 * @param {number} meters The value in meters.
 * @returns {number} The value in feet.
 */
export function metersToFeet(meters: number) {
  const conversionFactor = 3.28084 // 1 meter is approximately 3.28084 feet
  return meters * conversionFactor
}

export function isNumberOrZero(val: null | undefined | number): val is number {
  return typeof val === 'number' || typeof val === 'bigint'
}

const REPORT_STALENESS_HOURS = 2

/**
 * A report is stale if report_datetime is null or more than 2 hours old.
 * When stale, consumers should use NOAA forecast rolling totals instead.
 */
export function isReportStale(
  reportDatetime: string | null | undefined,
): boolean {
  if (!reportDatetime) return true
  return (
    differenceInHours(new Date(), new Date(reportDatetime)) >
    REPORT_STALENESS_HOURS
  )
}

export type SortOptionWithMinMax =
  | SortOption
  | '24hrMax'
  | '24hrMin'
  | 'depthMin'
  | 'depthMax'
  | 'elevation'
export function getMetricForLocation(
  location: LocationWithForecastAndReport,
  metric: SortOptionWithMinMax,
): number | null {
  if (location.type === 'snotel') {
    switch (metric) {
      case '12hr':
        return safeRound(location.report?.change12Hr ?? null, 1)
      case '24hr':
        return safeRound(location.report?.change24Hr ?? null, 1)
      case '24hrMax':
        return safeRound(location.report?.change24Hr ?? null, 1)
      case '24hrMin':
        return safeRound(location.report?.change24Hr ?? null, 1)
      case 'forecast24hr':
        return safeRound(location.forecast?.forecast24Hr, 1)
      case 'forecast7day':
        return safeRound(location.forecast?.forecast7Day, 1)
      case 'elevation':
        return safeRound(location.meta.elevation ?? null, 0)
      case 'depthMax':
        return safeRound(location.report?.currentDepth ?? null, 1)

      default:
        return null
    }
  }

  if (location.type === 'ski_resort') {
    const report = location.report
    const stale = isReportStale(report?.reportDatetime)

    // When report is stale, fall back to NOAA forecast rolling totals
    if (stale) {
      switch (metric) {
        case '12hr':
          return safeRound(location.forecast?.forecastPrev12Hr, 1)
        case '24hr':
        case '24hrMax':
        case '24hrMin':
          return safeRound(location.forecast?.forecastPrev24Hr, 1)
        case 'depthMax':
          return safeRound(report?.baseDepthMax, 1)
        case 'depthMin':
          return safeRound(report?.baseDepthMin, 1)
        case 'forecast24hr':
          return safeRound(location.forecast?.forecast24Hr, 1)
        case 'forecast7day':
          return safeRound(location.forecast?.forecast7Day, 1)
        case 'elevation':
          return safeRound(
            location.meta.summitElevation || location.meta.baseElevation,
            0,
          )
        default:
          return null
      }
    }

    const has24HrOverride = isNumberOrZero(report?.newSnow24HrOverride)

    const change24HrMin = has24HrOverride
      ? report!.newSnow24HrOverride!
      : report?.change24HrMin
    const change24HrMax = has24HrOverride
      ? report!.newSnow24HrOverride!
      : report?.change24HrMax

    const change24HrMinIsNumber = isNumberOrZero(change24HrMin)
    const change24HrMaxIsNumber = isNumberOrZero(change24HrMax)

    const bothNumbers = change24HrMaxIsNumber && change24HrMinIsNumber
    const oneIsNumber = change24HrMaxIsNumber || change24HrMinIsNumber

    const change12Hr = bothNumbers
      ? safeRound((change24HrMax + change24HrMin) / 4, 1)
      : oneIsNumber
        ? safeRound((change24HrMax ?? change24HrMin ?? 0) / 2, 1)
        : null

    switch (metric) {
      case '12hr':
        return change12Hr
      case '24hr':
        return bothNumbers
          ? safeRound((change24HrMax + change24HrMin) / 2, 1)
          : oneIsNumber
            ? safeRound(change24HrMax ?? change24HrMin, 1)
            : null
      case '24hrMax':
        return safeRound(
          has24HrOverride ? report!.newSnow24HrOverride : report?.change24HrMax,
          1,
        )
      case '24hrMin':
        return safeRound(
          has24HrOverride ? report!.newSnow24HrOverride : report?.change24HrMin,
          1,
        )
      case 'depthMax':
        return safeRound(report?.baseDepthMax, 1)

      case 'depthMin':
        return safeRound(report?.baseDepthMin, 1)

      case 'forecast24hr':
        return safeRound(location.forecast?.forecast24Hr, 1)
      case 'forecast7day':
        return safeRound(location.forecast?.forecast7Day, 1)
      case 'elevation':
        return safeRound(
          location.meta.summitElevation || location.meta.baseElevation,
          0,
        )

      default:
        return null
    }
  } else {
    switch (metric) {
      case '12hr':
        return safeRound(location.forecast?.forecastPrev12Hr, 1)
      case '24hr':
        return safeRound(location.forecast?.forecastPrev24Hr, 1)

      case '24hrMax':
        return safeRound(location.forecast?.forecastPrev24Hr, 1)
      case '24hrMin':
        return safeRound(location.forecast?.forecastPrev24Hr, 1)
      case 'forecast24hr':
        return safeRound(location.forecast?.forecast24Hr, 1)
      case 'forecast7day':
        return safeRound(location.forecast?.forecast7Day, 1)
      case 'elevation':
        return safeRound(location.meta.elevation, 0)

      default:
        return null
    }
  }
}

/**
 * Sort comparator for locations by metric value (descending)
 * Handles null values by putting them last
 */
export function sortLocationsByMetric(
  a: LocationWithForecastAndReport,
  b: LocationWithForecastAndReport,
  metric: SortOption,
): number {
  const aValue = getMetricForLocation(a, metric)
  const bValue = getMetricForLocation(b, metric)

  // Handle null values: non-null should come before null
  if (!isNumberOrZero(aValue) && isNumberOrZero(bValue)) return 1
  if (isNumberOrZero(aValue) && !isNumberOrZero(bValue)) return -1
  if (!isNumberOrZero(aValue) && !isNumberOrZero(bValue)) return 0

  // Both have values: sort by higher value first (descending)
  return bValue! - aValue!
}

/**
 * Break down a time period into hourly chunks with estimated values
 * @param validTime - String in format "2026-01-13T00:00:00+00:00/PT6H"
 * @param value - Total value for the entire period
 * @returns Array of hourly data points with estimated values
 */
export function breakdownTimeSeriesIntoHourlyChunks(
  validTime: string,
  value: number,
  transformVal?: (val: number, hours: number) => number,
): Array<{
  datetime: string
  value: number
  isEstimated?: boolean
  groupId?: string
}> {
  const [startTimeStr, durationStr] = validTime.split('/')
  const startTime = new Date(startTimeStr)

  // Parse duration (e.g., "PT6H" = 6 hours)
  const hoursMatch = durationStr.match(/PT(\d+)H/)
  const hours = hoursMatch ? parseInt(hoursMatch[1]) : 1

  // Generate a UUID for this group

  let isEstimated = false
  let val = value
  let groupId: string | null = null
  // Determine if values are estimated (more than 1 hour)

  if (transformVal && hours > 1) {
    val = transformVal(val, hours)
    groupId = crypto.randomUUID()
    isEstimated = true
  }

  // Create array of hourly chunks
  const chunks: Array<{
    datetime: string
    value: number
    isEstimated?: boolean
    groupId?: string
  }> = []

  for (let i = 0; i < hours; i++) {
    const datetime = new Date(startTime.getTime() + i * 60 * 60 * 1000)
    let chunk: {
      datetime: string
      value: number
      isEstimated?: boolean
      groupId?: string
    } = {
      datetime: datetime.toISOString(),
      value: val,
    }

    if (isEstimated && groupId) {
      chunk = { ...chunk, isEstimated, groupId }
    }
    chunks.push(chunk)
  }

  return chunks
}

/**
 * Group multiple time series data by datetime with flat structure
 * Only snowfall gets isEstimated and groupId fields
 * @param snowfalls - Array of snowfall data points (with optional isEstimated and groupId)
 * @param temps - Array of temperature data points
 * @param winds - Array of wind data points
 * @returns Array of grouped data by datetime with flat structure
 */
export function groupTimeSeriesByDatetime(
  snowfalls: Array<{
    datetime: string
    value: number
    isEstimated?: boolean
    groupId?: string
  }>,
  temps: Array<{
    datetime: string
    value: number
  }>,
  winds: Array<{
    datetime: string
    value: number
  }>,
): Array<{
  datetime: string
  snowfall?: number
  snowfall_is_estimated?: boolean
  snowfall_group_id?: string
  temp?: number
  wind_direction?: number
}> {
  // Create a map to group by datetime string
  const grouped = new Map<
    string,
    {
      datetime: string
      snowfall?: number
      snowfall_is_estimated?: boolean
      snowfall_group_id?: string
      temp?: number
      wind_direction?: number
    }
  >()

  // Add all unique datetimes to the map
  const allDatetimes = new Set([
    ...snowfalls.map((s) => s.datetime),
    ...temps.map((t) => t.datetime),
    ...winds.map((w) => w.datetime),
  ])

  allDatetimes.forEach((datetime) => {
    grouped.set(datetime, { datetime })
  })

  // Group snowfall data (only snowfall gets isEstimated and groupId)
  snowfalls.forEach((item) => {
    const entry = grouped.get(item.datetime)
    if (entry) {
      entry.snowfall = item.value
      if (item.isEstimated !== undefined) {
        entry.snowfall_is_estimated = item.isEstimated
      }
      if (item.groupId) {
        entry.snowfall_group_id = item.groupId
      }
    }
  })

  // Group temperature data
  temps.forEach((item) => {
    const entry = grouped.get(item.datetime)

    if (entry) {
      entry.temp = item.value
    }
  })

  // Group wind data
  winds.forEach((item) => {
    const entry = grouped.get(item.datetime)
    if (entry) {
      entry.wind_direction = item.value
    }
  })

  // Convert map to array and sort by datetime
  return Array.from(grouped.values()).sort((a, b) =>
    a.datetime.localeCompare(b.datetime),
  )
}

/**
 * Extract WFO (Weather Forecast Office) grid identifier from forecast URL
 * @param url - NOAA forecast URL
 * @returns WFO identifier (e.g., "BYZ/52,63") or null
 */
export function extractWfoFromUrl(url: string): string | null {
  // URL format: https://api.weather.gov/gridpoints/BYZ/52,63/forecast
  const match = url.match(/\/gridpoints\/([A-Z]+\/\d+,\d+)/)
  return match ? match[1] : null
}

function findMinMaxFromTwoNullableNums(
  first: number | null,
  second: number | null,
  operator: 'min' | 'max',
): number | null {
  const firstIsNum = isNumberOrZero(first)
  const secondIsNum = isNumberOrZero(second)

  const operatorFn = operator === 'max' ? Math.max : Math.min

  const result =
    firstIsNum && secondIsNum
      ? operatorFn(first, second)
      : firstIsNum && !secondIsNum
        ? first
        : secondIsNum && !firstIsNum
          ? second
          : null

  // Round temperatures to whole numbers (0 decimals)
  return safeRound(result, 0)
}

export function safeRoundNum(val: number | null, digits?: number) {
  const fixedDigits = digits || 0
  return isNumberOrZero(val) ? val.toFixed(fixedDigits) : null
}

export function groupDayAndNightForecasts(
  forecasts: BucketedForecast[],
): DailyForecast[] {
  const grouped = forecasts.reduce<Record<string, Partial<DailyForecast>>>(
    (acc, forecast) => {
      const { dateKey, isDaytime, summary } = forecast

      acc[dateKey] ??= { dateKey }

      // Assign day or night
      if (isDaytime) {
        acc[dateKey].day = forecast
      } else {
        acc[dateKey].night = forecast
      }

      // Full day min/max using helper
      acc[dateKey].fullDayMinTemp = findMinMaxFromTwoNullableNums(
        acc[dateKey].fullDayMinTemp ?? null,
        summary.tempMin,
        'min',
      )

      acc[dateKey].fullDayMaxTemp = findMinMaxFromTwoNullableNums(
        acc[dateKey].fullDayMaxTemp ?? null,
        summary.tempMax,
        'max',
      )

      // Snow total: sum only if numbers
      const currentSnow = isNumberOrZero(summary.snowTotal)
        ? summary.snowTotal
        : 0
      const totalSnowfall = (acc[dateKey].fullDaySnowfall ?? 0) + currentSnow
      acc[dateKey].fullDaySnowfall = safeRound(totalSnowfall, 1) ?? 0

      return acc
    },
    {},
  )

  return Object.values(grouped) as DailyForecast[]
}

export function getTotalSnowFallForPeriods(periods: DailyForecast[]) {
  const total = periods.reduce((sum, per) => {
    return sum + (per.fullDaySnowfall ?? 0)
  }, 0)
  return safeRound(total, 1) ?? 0
}
export function getWeekOfForecasts(
  forecasts: BucketedForecast[],
  week: 'current' | 'next' | 'previous',
): DailyForecast[] {
  const currentDayPeriodIdx = forecasts.findIndex(
    (forecast) => forecast.isCurrentDay && forecast.isDaytime,
  )

  // if it doesnt exist return nothing becuase there will be no data
  if (currentDayPeriodIdx === -1) {
    return []
  }

  if (week === 'current') {
    return groupDayAndNightForecasts(
      forecasts.slice(currentDayPeriodIdx, currentDayPeriodIdx + 14),
    )
  }

  if (week === 'next') {
    return groupDayAndNightForecasts(
      forecasts.slice(currentDayPeriodIdx + 14, currentDayPeriodIdx + 28),
    )
  }

  return groupDayAndNightForecasts(
    forecasts.slice(Math.max(0, currentDayPeriodIdx - 14), currentDayPeriodIdx),
  )
}

export function combineNumericForecastWithText(
  forecasts: DailyForecast[],
  textForecast: NOAATextForecastResponse | null | undefined,
  timezone: string,
): DailyForecast[] {
  if (!textForecast) {
    return forecasts
  }

  const updatedForecasts = [...forecasts]
  textForecast.properties.periods.forEach((textPeriod) => {
    const textIsDayTime = textPeriod.isDaytime
    const dayNightKey = textIsDayTime ? 'day' : 'night'
    const textEndDate = parseISO(textPeriod.endTime, { in: tz(timezone) })

    const hour = getHours(textEndDate, { in: tz(timezone) })
    let textDateKey

    // If endtime is 6AM (or earlier) but on the next day, then the datekey is the previous day
    if (hour <= 6 && hour > 0) {
      textDateKey = format(
        subDays(textEndDate, 1, { in: tz(timezone) }),
        'yyyy-MM-dd',
        { in: tz(timezone) },
      )
    } else {
      textDateKey = format(textEndDate, 'yyyy-MM-dd', { in: tz(timezone) })
    }

    const matchingForecastPeriodIdx = updatedForecasts.findIndex(
      (fc) => fc.dateKey === textDateKey,
    )

    if (matchingForecastPeriodIdx !== -1) {
      const matchingForecastPeriod = updatedForecasts[matchingForecastPeriodIdx]
      const newForecast: DailyForecast = {
        ...matchingForecastPeriod,
        [dayNightKey]: {
          ...matchingForecastPeriod[dayNightKey],
          textForecast: textPeriod,
        },
      }

      updatedForecasts[matchingForecastPeriodIdx] = newForecast
    }
  })

  return updatedForecasts
}
